// components/discovery/GameDetailsModal.jsx
import React, { useEffect, useState } from 'react';
import { X, Users, Heart, TrendingUp, DollarSign, ExternalLink } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const GameDetailsModal = ({ game, onClose, cachedDetails = null, onDetailsLoaded = null }) => {
    const [details, setDetails] = useState(cachedDetails);
    const [loading, setLoading] = useState(!cachedDetails);
    const steamUrl = `https://store.steampowered.com/app/${game.steamId}`;

    const classificationDisplay = game.publisherClassDisplay 
        || game.publisherClassification?.display
        || game.publisherClass
        || game.publisherComputedClass
        || 'Hobbyist';

    const classificationDelta = game.publisherClassification?.delta;
    const classificationTone = classificationDelta === 'mismatch' ? 'text-yellow-600' : 'text-gray-600';

    useEffect(() => {
        const fetchGameDetails = async () => {
            try {
                const response = await fetch(`/analytics/api/game/${game.steamId}`);
                const data = await response.json();

                // If no visuals in the response, try Gamalytic visual-assets paths
                let headerImageUrl = data?.headerImageUrl;
                let capsuleImageUrl = data?.capsuleImageUrl;

                const fetchVisual = async (type) => {
                    try {
                        // Proxy through backend so the API key is applied (uses gamalyticService.fetchWithAuth)
                        const res = await fetch(`/analytics/api/gamalytic/visual-assets/${type}/${game.steamId}`);
                        const data = await res.json();
                        if (data?.urls && data.urls.length > 0) {
                            return data.urls[0];
                        }
                    } catch (e) {
                        return null;
                    }
                    return null;
                };

                if (!headerImageUrl) {
                    headerImageUrl = await fetchVisual('header');
                }
                // Skip capsule fallback for now

                const enriched = {
                    ...data,
                    headerImageUrl,
                    capsuleImageUrl
                };

                setDetails(enriched);
                if (onDetailsLoaded) {
                    onDetailsLoaded(enriched);
                }
            } catch (error) {
                console.error('Error fetching game details:', error);
            } finally {
                setLoading(false);
            }
        };

        if (!cachedDetails) {
            fetchGameDetails();
        }
    }, [game.steamId, cachedDetails, onDetailsLoaded]);

    const formatNumber = (num) => {
        if (num == null || !Number.isFinite(num)) return '—';
        if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
        return num.toLocaleString();
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-8 max-w-4xl w-full mx-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                </div>
            </div>
        );
    }

    const genresToShow = details?.genres || game.genres || [];
    const tagsToShow = details?.tags || game.tags || [];
    const headerImageUrl = details?.headerImageUrl || details?._metadata?.headerImageUrl || null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 my-8 overflow-hidden">
                {/* Banner */}
                {headerImageUrl && (
                    <img
                        src={headerImageUrl}
                        alt={`${game.name} header`}
                        className="w-full h-52 md:h-64 object-cover"
                    />
                )}

                {/* Header */}
                <div className="p-6 border-b flex justify-between items-start">
                    <div>
                        <h2 className="text-2xl font-bold">{game.name}</h2>
                        <div className="text-gray-600 mt-1">
                            {(() => {
                                const publisherName = game.publishers?.[0]?.name || game.publishers?.[0];
                                const developerName = game.developers?.[0]?.name || game.developers?.[0];
                                
                                // If both exist and are different, show both
                                if (publisherName && developerName && publisherName !== developerName) {
                                    return (
                                        <div>
                                            <div className="font-medium">{publisherName}</div>
                                            <div className="text-sm text-gray-500">Developer: {developerName}</div>
                                        </div>
                                    );
                                }
                                // If they're the same or only one exists, show just one
                                else if (publisherName || developerName) {
                                    return <div>{publisherName || developerName}</div>;
                                }
                                // Fallback
                                else {
                                    return <div>Unknown Publisher</div>;
                                }
                            })()}
                            <div className={`text-sm ${classificationTone} mt-1`}>Classification: {classificationDisplay}</div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-gray-100 rounded"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-8">
                    {/* Key Metrics */}
                    <div className="grid grid-cols-4 gap-4">
                        <MetricCard
                            icon={<Heart className="w-5 h-5 text-red-500" />}
                            label="Wishlists"
                            value={formatNumber(details?.wishlists ?? game.wishlists)}
                        />
                        <MetricCard
                            icon={<Users className="w-5 h-5 text-blue-500" />}
                            label="Followers"
                            value={formatNumber(details?.followers ?? game.followers)}
                        />
                        <MetricCard
                            icon={<TrendingUp className="w-5 h-5 text-green-500" />}
                            label="Review Score"
                            value={`${details?.reviewScore ?? game.reviewScore ?? 0}%`}
                        />
                        <MetricCard
                            icon={<DollarSign className="w-5 h-5 text-yellow-500" />}
                            label="Price"
                            value={(() => {
                                const price = details?.price ?? game.price;
                                return price === 0 ? 'Free' : price != null ? `$${price.toFixed(2)}` : '—';
                            })()}
                        />
                    </div>

                    {/* Growth Chart */}
                    {details?.history && (
                        <div>
                            <h3 className="text-lg font-medium mb-4">Growth Trends</h3>
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={details.history}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis 
                                            dataKey="timeStamp" 
                                            tickFormatter={(timestamp) => {
                                                return new Date(timestamp).toLocaleDateString();
                                            }}
                                        />
                                        <YAxis />
                                        <Tooltip 
                                            labelFormatter={(timestamp) => {
                                                return new Date(timestamp).toLocaleDateString();
                                            }}
                                        />
                                        <Line type="monotone" dataKey="wishlists" stroke="#3B82F6" name="Wishlists" />
                                        <Line type="monotone" dataKey="followers" stroke="#10B981" name="Followers" />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}

                    {/* All Genres and Tags */}
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <h3 className="text-lg font-medium mb-3">All Genres</h3>
                            <div className="flex flex-wrap gap-2">
                                {genresToShow.map(genre => (
                                    <span 
                                        key={genre}
                                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                                    >
                                        {genre}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div>
                            <h3 className="text-lg font-medium mb-3">All Tags</h3>
                            <div className="flex flex-wrap gap-2">
                                {tagsToShow.map(tag => (
                                    <span 
                                        key={tag}
                                        className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Steam Link */}
                    <div className="flex justify-end border-t pt-4">
                        <a 
                            href={steamUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            <ExternalLink className="w-4 h-4" />
                            View on Steam
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

const MetricCard = ({ icon, label, value }) => (
    <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
            {icon}
            <span className="text-sm text-gray-600">{label}</span>
        </div>
                        <div className="text-xl font-bold">{value}</div>
    </div>
);

export default GameDetailsModal;
